import React, { useState, useMemo, Fragment } from 'react'
import { withRouter } from 'react-router'
import { Row, Col } from 'react-flexbox-grid'
import { isNumeric } from 'validator'
import barcoder from 'barcoder'
import { useTranslation } from 'react-i18next'
import get from 'lodash/get'

import { useQuery } from 'components/graphql'
import { Form, Checkbox, TextArea } from 'components/forms'
import { ContentBlock } from 'components/ContentBlock'
import DxDataTable from 'components/DxDataTable'
import { hardwareDx } from '../../tableColumns'
import { listProductsWithEANCode } from '../../gql/query'
import { appConfig } from 'gql/query'

const Hardware = ({ data, formData, mutations: { updateItem }, history, match }) => {
	const [expanded, setExpanded] = useState([1])
	const [checkDigit, setCheckDigit] = useState(null)
	const [t] = useTranslation()

	const { data: EANProdsData } = useQuery(listProductsWithEANCode, {
		fetchPolicy: 'cache-and-network',
		variables: { filter: { EANCode: 'notEmpty' }, params: { sort: { field: 'isActive', order: 'DESC' } } }
	})
	const EANProds = get(EANProdsData, 'listProducts.items', [])

	const { data: configData } = useQuery(appConfig.taraName, {
		fetchPolicy: 'cache-and-network'
	})

	const taraName = configData?.getAppConfig?.taraName || []
	const isActiveTares = configData?.getAppConfig?.taraName.some(item => item)

	const handleSwitch = (switchVal, ind) => {
		setExpanded(switchVal ? [...expanded, ind] : expanded.filter(item => item !== ind))
	}

	const calculateCheckNum = codeStr => {
		const code = parseInt(codeStr, 10).toString()
		const chunks = code.split('').map(val => (parseInt(val, 10))).reverse()
		let checksum = 0

		// sum numbers and multiply accordingly
		chunks.forEach(function (number, index) {
			checksum += (index % 2) === 1 ? number : number * 3
		})

		// calc checksum
		checksum %= 10
		checksum = (checksum === 0) ? 0 : (10 - checksum)

		return checksum
	}

	const EANValidate = val => {
		if(!val) {
			return null
		}
		const placeholderPrices = []
		const valArr = val.split('\n')
		const hasPlaceholderPrice = (code, i, letter) => {
			if(code.length > 8) {
				const hasLetter = code.indexOf(letter) >= 7
				if(hasLetter) {
					placeholderPrices.push(i)
				}
				return hasLetter
			}
			return code.substr(-6, 6) === letter.repeat(6)
		}
		const isNum = !valArr.some((item, i) => {
			if(hasPlaceholderPrice(item, i, 'P')) {
				return null
			}
			if(hasPlaceholderPrice(item, i, 'G')) {
				return null
			}
			return !isNumeric(item, { no_symbols: true })
		})
		if(!isNum) {
			setCheckDigit(null)
			return t('product_section.strings.codes_must_int')
		}
		const codeTip = valArr.find((item, i) => (placeholderPrices.indexOf(i) === -1 && (item.length === 7 || item.length === 12)))
		if(codeTip) {
			const checkNum = calculateCheckNum(codeTip)
			setCheckDigit(checkNum + '')
		} else {
			setCheckDigit(null)
		}
		const isLength = !valArr.some(item => !(item.length === 8 || item.length === 13))
		if(!isLength) {
			return t('product_section.strings.codes_must_digits')
		}
		const isEAN = !valArr.some((item, i) => placeholderPrices.indexOf(i) === -1 && !barcoder.validate(item))
		if(!isEAN) {
			return t('product_section.strings.invalid_ean')
		}
		const repeatError = valArr.reduce((err, item) => {
			if(err) {
				return err
			}
			if(item.length === 8 || item.length === 13) {
				const repeatProd = EANProds.find(({ id, EANCode }) => {
					return id === data.id ? false : EANCode.some(prodCode => prodCode === item)
				})
				if(repeatProd) {
					return (
						<span>
							{t('product_section.strings.code')} {item} {t('product_section.strings.already_in_use')} <i
								onClick={e => history.push(`${repeatProd.id}`)} style={{ cursor: 'pointer', textDecoration: 'underline' }}>{repeatProd.name}</i>
						</span>
					)
				}
			}
			return err
		}, '')
		if(repeatError) {
			return repeatError
		}
		return null
	}

	if(Array.isArray(formData.EANCode)) {
		formData.EANCode = formData.EANCode.join('\n')
	}

	const tableCols = useMemo(() => hardwareDx(t), [])

	return(
		<Form initialValues={formData} onSubmit={vals => updateItem({ ...vals, ...vals.EANCode && { EANCode: vals.EANCode.split('\n') } })} smartSubmit className='flex-col-container'>
			{({ formState: values = {} }) => (
				<Fragment>
					<ContentBlock>
						<Row>
							<Col xs>
								<p className='form-group-label'>{t('product_section.strings.scales')}</p>
								<Checkbox field='isWeighted' label={t('product_section.strings.weight_on_scales')} />
								<Checkbox field='isTareInLabelMode' label={t('product_section.strings.tare_for_labels')} />
							</Col>
							<Col xs>
								{isActiveTares ?
									<Fragment>
										<p className='form-group-label'>{t('product_section.strings.select_tare')}</p>
										{taraName.map((name, i) => name ? <Checkbox field={`tare[${i}]`} label={name} key={i} /> : null)}
									</Fragment>
									: null
								}
							</Col>
						</Row>
					</ContentBlock>

					<ContentBlock
						label={t('product_section.strings.ean')}
						switching
						onSwitch={val => handleSwitch(val, 1)}
						expanded={expanded.indexOf(1) > -1}
					>
						<TextArea
							field='EANCode'
							label={t('product_section.strings.capture_code_scanner')}
							validate={EANValidate}
							validateOnChange
							withInfo
							infoText={t('product_section.strings.ean_field_info')}
							style={{ maxWidth: 'none' }}
							{...(checkDigit || checkDigit === 0) && { forceInfoText: `${t('product_section.strings.check_digit')}: ${checkDigit}` }}
							height={72} />
						{/* <Select
							label='Auswahl'
							field='EANstatistics'
							options={[
								{ value: 0, label: 'Stück' },
								{ value: 1, label: 'kg.' },
								{ value: 2, label: 'Emulieren eine Kundenkarte' }
							]}
						/> */}
					</ContentBlock>

					<ContentBlock
						label={t('product_section.strings.ean_products')}
						switching
						onSwitch={val => handleSwitch(val, 2)}
						expanded={expanded.indexOf(2) > -1}
					>
						<DxDataTable
							data={EANProds}
							columns={tableCols}
							height={372}
							withSearch
							columnAutoWidth={false}
						/>
					</ContentBlock>
				</Fragment>
			)}
		</Form>
	)
}

export default withRouter(Hardware)
